import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Container,
  FormControlLabel,
  LinearProgress,
  Paper,
  Stack,
  Step,
  StepContent,
  StepLabel,
  Stepper,
  TextField,
  Typography
} from '@mui/material';
import {
  AccountBalanceWallet as WalletIcon,
  CheckCircle as CheckCircleIcon,
  OpenInNew as OpenInNewIcon,
  AutoAwesome as AssistantIcon
} from '@mui/icons-material';
import { doc, onSnapshot } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import PageHeader from '../common/PageHeader';
import { db, functions } from '../../firebase/config';
import { selectCanManageGlobalSettings } from '../../redux/slices/authSlice';
import { useTransportWording } from '../../utils/transportWording';

/**
 * Geführter Einrichtungs-Assistent für das eigene PayPal-Konto
 * (docs/paypal_app_zahlungen_konzept.md, 8.6). Führt den Unternehmer in
 * wenigen Schritten durch: Geschäftskonto prüfen → Entwickler-App anlegen →
 * Zugangsdaten verbinden (Callable `connectPayPalCredentials`, erkennt die
 * Umgebung automatisch). Die gesamte Einrichtung dauert ca. 5 Minuten.
 */
const TOTAL_STEPS = 4;

const PayPalSetupGuidePage = () => {
  const navigate = useNavigate();
  const wording = useTransportWording();
  const canManage = useSelector(selectCanManageGlobalSettings);

  const [activeStep, setActiveStep] = useState(0);
  const [hasBusinessAccount, setHasBusinessAccount] = useState(false);
  const [hasCreatedApp, setHasCreatedApp] = useState(false);

  // Verbindungsstatus live aus appSettings/payments
  const [paypalConnected, setPaypalConnected] = useState(false);
  const [clientIdMasked, setClientIdMasked] = useState('');
  const [connectedEnvironment, setConnectedEnvironment] = useState('sandbox');
  const [accountEmailDisplay, setAccountEmailDisplay] = useState('');
  const [appPaymentsEnabled, setAppPaymentsEnabled] = useState(false);

  // Verbinden-Formular
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [accountEmail, setAccountEmail] = useState('');
  const [connecting, setConnecting] = useState(false);
  const [activating, setActivating] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const unsubscribe = onSnapshot(doc(db, 'appSettings', 'payments'), (snapshot) => {
      const data = snapshot.exists() ? snapshot.data() : {};
      const connected = data.paypalConnected === true;
      setPaypalConnected(connected);
      setClientIdMasked(data.paypalClientIdMasked || '');
      setConnectedEnvironment(data.environment === 'live' ? 'live' : 'sandbox');
      setAccountEmailDisplay(data.paypalAccountEmail || '');
      setAppPaymentsEnabled(data.appPaymentsEnabled === true);
    }, () => undefined);
    return unsubscribe;
  }, []);

  // Bei bereits verbundenem Konto direkt zum Abschluss springen (einmalig)
  const [jumpedToEnd, setJumpedToEnd] = useState(false);
  useEffect(() => {
    if (paypalConnected && !jumpedToEnd) {
      setHasBusinessAccount(true);
      setHasCreatedApp(true);
      setActiveStep(3);
      setJumpedToEnd(true);
    }
  }, [paypalConnected, jumpedToEnd]);

  const completedCount = useMemo(() => {
    let count = 0;
    if (hasBusinessAccount) count += 1;
    if (hasCreatedApp) count += 1;
    if (paypalConnected) count += 2; // Verbinden + Abschluss
    return Math.min(count, TOTAL_STEPS);
  }, [hasBusinessAccount, hasCreatedApp, paypalConnected]);

  const handleConnect = async () => {
    setConnecting(true);
    setError(null);
    try {
      const callable = httpsCallable(functions, 'connectPayPalCredentials');
      await callable({
        clientId: clientId.trim(),
        clientSecret: clientSecret.trim(),
        accountEmail: accountEmail.trim()
      });
      setClientId('');
      setClientSecret('');
      setAccountEmail('');
      setActiveStep(3);
    } catch (err) {
      setError(err?.message || 'Verbindung fehlgeschlagen. Bitte Client-ID und Secret prüfen.');
    } finally {
      setConnecting(false);
    }
  };

  const handleActivate = async () => {
    setActivating(true);
    setError(null);
    try {
      const callable = httpsCallable(functions, 'configurePayPalPayments');
      await callable({ enabled: true });
    } catch (err) {
      setError(err?.message || 'Aktivierung fehlgeschlagen. Bitte erneut versuchen.');
    } finally {
      setActivating(false);
    }
  };

  const goBackToSettings = () => navigate('/einstellungen');

  if (!canManage) {
    return (
      <Container maxWidth="md" sx={{ py: 3 }}>
        <PageHeader
          title="PayPal-Konto einrichten"
          breadcrumbs={[
            { label: 'Dashboard', path: '/dashboard' },
            { label: 'Einstellungen', path: '/einstellungen' },
            { label: 'PayPal-Konto einrichten' }
          ]}
        />
        <Alert severity="info">
          App-Zahlungen werden von der Hauptzentrale verwaltet. Bitte wenden Sie
          sich an Ihre Zentrale, um das PayPal-Konto einzurichten.
        </Alert>
      </Container>
    );
  }

  const navigation = (canContinue = true) => (
    <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
      <Button variant="contained" onClick={() => setActiveStep((s) => s + 1)} disabled={!canContinue}>
        Weiter
      </Button>
      {activeStep > 0 && <Button onClick={() => setActiveStep((s) => s - 1)}>Zurück</Button>}
    </Stack>
  );

  return (
    <Container maxWidth="md" sx={{ py: 3 }}>
      <PageHeader
        title="PayPal-Konto einrichten"
        description="In wenigen Schritten Ihr PayPal-Geschäftskonto verbinden"
        breadcrumbs={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Einstellungen', path: '/einstellungen' },
          { label: 'PayPal-Konto einrichten' }
        ]}
      />

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack spacing={1.5}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              justifyContent="space-between"
              alignItems={{ xs: 'flex-start', sm: 'center' }}
              spacing={1}
            >
              <Box>
                <Typography variant="h6">
                  <AssistantIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                  Einrichtung in ca. 5 Minuten
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {completedCount} von {TOTAL_STEPS} Schritten abgeschlossen
                </Typography>
              </Box>
              {paypalConnected ? (
                <Chip
                  icon={<CheckCircleIcon />}
                  color="success"
                  label={`Verbunden · ${connectedEnvironment === 'live' ? 'Live' : 'Sandbox'}`}
                />
              ) : (
                <Chip icon={<WalletIcon />} label="Noch nicht verbunden" color="warning" />
              )}
            </Stack>
            <LinearProgress variant="determinate" value={(completedCount / TOTAL_STEPS) * 100} />
          </Stack>
        </CardContent>
      </Card>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Paper elevation={2} sx={{ p: { xs: 2, sm: 3 } }}>
        <Stepper activeStep={activeStep} orientation="vertical" nonLinear>
          {/* Schritt 1 */}
          <Step completed={hasBusinessAccount}>
            <StepLabel onClick={() => setActiveStep(0)} sx={{ cursor: 'pointer' }}>
              PayPal-Geschäftskonto bereithalten
            </StepLabel>
            <StepContent>
              <Stack spacing={2}>
                <Typography variant="body2" color="text.secondary">
                  Für App-Zahlungen benötigen Sie ein <strong>verifiziertes
                  PayPal-Geschäftskonto</strong>. Es ist kostenlos; ein privates
                  Konto lässt sich kostenfrei umwandeln. „Verifiziert" bedeutet:
                </Typography>
                <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                  <li>E-Mail-Adresse bestätigt</li>
                  <li>Bankkonto hinterlegt</li>
                  <li>Identitätsprüfung (KYC) abgeschlossen</li>
                </Box>
                <Box>
                  <Button
                    component="a"
                    href="https://www.paypal.com/de/business"
                    target="_blank"
                    rel="noreferrer"
                    variant="outlined"
                    startIcon={<OpenInNewIcon />}
                  >
                    PayPal-Geschäftskonto öffnen / anlegen
                  </Button>
                </Box>
                <FormControlLabel
                  control={<Checkbox checked={hasBusinessAccount} onChange={(e) => setHasBusinessAccount(e.target.checked)} />}
                  label="Ich habe ein verifiziertes PayPal-Geschäftskonto"
                />
                {navigation(hasBusinessAccount)}
              </Stack>
            </StepContent>
          </Step>

          {/* Schritt 2 */}
          <Step completed={hasCreatedApp}>
            <StepLabel onClick={() => setActiveStep(1)} sx={{ cursor: 'pointer' }}>
              Entwickler-App anlegen
            </StepLabel>
            <StepContent>
              <Stack spacing={2}>
                <Typography variant="body2" color="text.secondary">
                  Melden Sie sich mit <strong>demselben Geschäftskonto</strong> im
                  PayPal-Entwicklerbereich an und legen Sie eine App an — das
                  erzeugt die Zugangsdaten, mit denen NextCab Zahlungen für Sie
                  abwickelt.
                </Typography>
                <Box component="ol" sx={{ m: 0, pl: 2.5 }}>
                  <li>Auf <strong>developer.paypal.com</strong> mit dem Geschäftskonto anmelden.</li>
                  <li>Bereich <strong>„Apps &amp; Credentials"</strong> öffnen und auf <strong>„Live"</strong> umschalten (für echte Zahlungen).</li>
                  <li>Auf <strong>„Create App"</strong> klicken, einen Namen vergeben (z. B. „{wording.vehicle}-App") und bestätigen.</li>
                </Box>
                <Box>
                  <Button
                    component="a"
                    href="https://developer.paypal.com/dashboard/applications/live"
                    target="_blank"
                    rel="noreferrer"
                    variant="outlined"
                    startIcon={<OpenInNewIcon />}
                  >
                    PayPal-Entwicklerbereich öffnen
                  </Button>
                </Box>
                <FormControlLabel
                  control={<Checkbox checked={hasCreatedApp} onChange={(e) => setHasCreatedApp(e.target.checked)} />}
                  label="Ich habe eine App angelegt"
                />
                {navigation(hasCreatedApp)}
              </Stack>
            </StepContent>
          </Step>

          {/* Schritt 3 */}
          <Step completed={paypalConnected}>
            <StepLabel onClick={() => setActiveStep(2)} sx={{ cursor: 'pointer' }}>
              Zugangsdaten verbinden
            </StepLabel>
            <StepContent>
              <Stack spacing={2}>
                <Typography variant="body2" color="text.secondary">
                  Kopieren Sie aus Ihrer PayPal-App <strong>Client-ID</strong> und{' '}
                  <strong>Secret</strong> und fügen Sie beide hier ein. Die Daten
                  werden geprüft, sicher gespeichert und die PayPal-Benachrichtigungen
                  automatisch eingerichtet. Die Umgebung (Live oder Sandbox) wird
                  automatisch erkannt.
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  label="Client-ID"
                  value={clientId}
                  onChange={(e) => setClientId(e.target.value)}
                  disabled={connecting}
                  autoComplete="off"
                />
                <TextField
                  fullWidth
                  size="small"
                  type="password"
                  label="Secret"
                  value={clientSecret}
                  onChange={(e) => setClientSecret(e.target.value)}
                  disabled={connecting}
                  autoComplete="new-password"
                />
                <TextField
                  fullWidth
                  size="small"
                  type="email"
                  label="PayPal-E-Mail-Adresse (optional, nur zur Anzeige)"
                  placeholder="ihr-konto@beispiel.de"
                  value={accountEmail}
                  onChange={(e) => setAccountEmail(e.target.value)}
                  helperText="Wird nur angezeigt, damit sichtbar ist, wohin das Geld fließt."
                  disabled={connecting}
                />
                <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                  <Button
                    variant="contained"
                    onClick={handleConnect}
                    disabled={connecting || !clientId.trim() || !clientSecret.trim()}
                    startIcon={connecting ? <CircularProgress size={16} /> : null}
                  >
                    {connecting ? 'Wird geprüft…' : 'Verbinden & prüfen'}
                  </Button>
                  <Button onClick={() => setActiveStep(1)} disabled={connecting}>Zurück</Button>
                </Stack>
              </Stack>
            </StepContent>
          </Step>

          {/* Schritt 4 */}
          <Step completed={paypalConnected}>
            <StepLabel onClick={() => paypalConnected && setActiveStep(3)} sx={{ cursor: paypalConnected ? 'pointer' : 'default' }}>
              Fertig
            </StepLabel>
            <StepContent>
              <Stack spacing={2}>
                <Alert
                  severity="success"
                  action={
                    <Chip
                      size="small"
                      color={connectedEnvironment === 'live' ? 'success' : 'warning'}
                      label={connectedEnvironment === 'live' ? 'Live' : 'Sandbox (Test)'}
                    />
                  }
                >
                  PayPal-Konto verbunden{clientIdMasked ? `: ${clientIdMasked}` : ''}.
                  <br />
                  Zahlungen gehen an:{' '}
                  <strong>{accountEmailDisplay || 'Ihr verbundenes PayPal-Geschäftskonto'}</strong>
                </Alert>

                {appPaymentsEnabled ? (
                  <Alert severity="info">
                    App-Zahlungen sind aktiv. Kunden können bei der Bestellung
                    PayPal wählen.
                  </Alert>
                ) : (
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Letzter Schritt: App-Zahlungen aktivieren. Dabei werden
                      Fahrpreis-Anzeige und Preiseingabe automatisch passend
                      eingestellt.
                    </Typography>
                    <Button
                      variant="contained"
                      onClick={handleActivate}
                      disabled={activating}
                      startIcon={activating ? <CircularProgress size={16} /> : <WalletIcon />}
                    >
                      {activating ? 'Wird aktiviert…' : 'App-Zahlungen jetzt aktivieren'}
                    </Button>
                  </Box>
                )}

                <Box>
                  <Button variant="outlined" onClick={goBackToSettings}>
                    Zu den Einstellungen
                  </Button>
                </Box>
              </Stack>
            </StepContent>
          </Step>
        </Stepper>
      </Paper>
    </Container>
  );
};

export default PayPalSetupGuidePage;
